import { iframeDataBridge } from '@/iframe/iframeDataBridge';

// Product detail rendered by Vue (ProductDetailDialog) from what the decommissioned legacy product modal
// (decommissioned/m.eagle/app/components/modals/productAssortDetail.js) read : the catalogs the legacy synchronized,
// assortment first (relevé / promo products) then the sales catalog. The objects come from the iframe realm and are
// never mutated : everything returned is a plain copy, read once when the dialog opens (the legacy data is not reactive).

const EMPTY_UID = '00000000-0000-0000-0000-000000000000';

const legacyWindow = () => iframeDataBridge.getIframeWindow();
const ecosVMOf = () => legacyWindow()?.externis?.ecosVM || null;

/** A legacy collection, plain array or Knockout observable, never throwing. */
function listOf(source) {
	try {
		const list = typeof source === 'function' ? source() : source;
		return Array.isArray(list) ? list : [];
	} catch {
		return [];
	}
}

const uidOf = (uid) => (uid && uid !== EMPTY_UID ? uid : null);
const positiveOf = (value) => (Number(value) > 0 ? Number(value) : null);

function findProduct(ecosVM, productId) {
	const byId = (product) => product?.id === productId;
	return listOf(ecosVM?.productsAssort).find(byId) || listOf(ecosVM?.products).find(byId) || null;
}

function findDocument(ecosVM, documentId) {
	return listOf(ecosVM?.documents).find((doc) => doc?.id === documentId) || null;
}

/** Documents linked to the product (`ecosVM.productsDocuments`) and published today, as documentsList.js lists them. */
function documentsOf(ecosVM, productId) {
	const ids = listOf(listOf(ecosVM?.productsDocuments).find((entry) => entry?.productId === productId)?.documents);
	if (!ids.length) return [];
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	return listOf(ecosVM?.documents)
		.filter((doc) => ids.includes(doc?.id) && new Date(doc.startDate) <= today && new Date(doc.endDate) >= today)
		.map((doc) => {
			const fileName = listOf(doc.versions)[0]?.fileName || '';
			return { id: doc.id, label: doc.label || fileName, fileName };
		});
}

/** Bill of materials (`productsIncluded`) : each component with its quantity and container, labelled like the legacy modal. */
function componentsOf(ecosVM, product) {
	const containers = listOf(ecosVM?.productContainers);
	return listOf(product.productsIncluded).map((line) => {
		const component = findProduct(ecosVM, line.productId);
		return {
			productId: line.productId,
			name: component?.name || 'N/A',
			ean: component?.ean || '',
			imageThumbnailUid: uidOf(component?.imageThumbnailUid),
			quantity: Number(line.quantity) || 0,
			container: containers.find((container) => container?.id === line.productContainerId)?.label || '',
		};
	});
}

/**
 * Everything the product detail shows, or null when the product is in neither synchronized catalog.
 * Dimensions are null unless strictly positive : the legacy modal hid a zero weight / height / width / length.
 */
export function readProductDetail(productId) {
	const ecosVM = ecosVMOf();
	const product = findProduct(ecosVM, Number(productId));
	if (!product) return null;
	return {
		id: product.id,
		name: product.name || '',
		reference: product.reference || '',
		ean: product.ean || '',
		customerCode: product.customerCode || '',
		description: product.description || '',
		imageUid: uidOf(product.imageUid),
		imageThumbnailUid: uidOf(product.imageThumbnailUid),
		weight: positiveOf(product.weight),
		height: positiveOf(product.height),
		width: positiveOf(product.width),
		length: positiveOf(product.length),
		documents: documentsOf(ecosVM, product.id),
		components: componentsOf(ecosVM, product),
	};
}

/** documentsList.js clickDocumentHandler() : opens the legacy document viewer (`evtDocument`, #mdDocument). */
export function openProductDocument(documentId) {
	const win = legacyWindow();
	const docHelper = win?.externis?.doc?.docHelper;
	const doc = findDocument(win?.externis?.ecosVM, documentId);
	if (!doc || !docHelper || !win.amplify) return false;
	try {
		win.amplify.publish('evtDocument', win.btoa(JSON.stringify({ obj: docHelper.getUniqueId(doc) })));
		return true;
	} catch {
		return false;
	}
}

/** documentsList.js downloadHandler() : the last version, downloaded in chunks by docHelper. */
export function downloadProductDocument(documentId) {
	const win = legacyWindow();
	const docHelper = win?.externis?.doc?.docHelper;
	const doc = findDocument(win?.externis?.ecosVM, documentId);
	if (!doc || !docHelper) return false;
	const version = listOf(doc.versions)[0];
	try {
		docHelper.downloadFile(`${docHelper.pxKeyFile}${doc.id}`, version?.fileName, version?.chunkSize, version?.totalChunks);
		return true;
	} catch {
		return false;
	}
}
